import { LitElement, html, css } from 'lit';

/**
 * CrusherConnectionStatus
 *
 * Inline status indicator for any external connection (OAuth integrations,
 * databases, webhooks, third-party services). Five canonical states:
 *
 *   - connected         — green dot + "Connected"  + optional `since`
 *   - disconnected      — muted dot + "Not connected"
 *   - reauth-required   — amber dot + "Re-authorize required"
 *   - error             — red dot + "Connection error" + `errorMessage`
 *   - connecting        — animated brand dot + "Connecting…"
 *
 * Attributes:
 *   - provider       (string)   — required-ish; the service name (e.g. "QuickBooks Online")
 *   - state          (enum)     — one of the five above; defaults to "disconnected"
 *   - since          (string)   — optional; ISO date or human relative
 *   - error-message  (string)   — optional; shown when state="error"
 *   - compact        (boolean)  — tighter layout (single-line, no meta row)
 *
 * Slots:
 *   - cta            — connect/reconnect button, link, etc. Renders to the right of the label.
 *
 * Events: none. This is a status surface, not an interactive control. Wire the
 * CTA slot's button to your own click handler.
 *
 * Tokens used: --crusher-feedback-{success,warning,danger}, --crusher-color-brand-primary,
 *   --crusher-fg-muted, --crusher-surface, --crusher-border, plus the standard
 *   density / spacing / radius scale.
 */
export class CrusherConnectionStatus extends LitElement {
  static properties = {
    provider:     { type: String, reflect: true },
    state:        { type: String, reflect: true },
    since:        { type: String, reflect: true },
    errorMessage: { type: String, attribute: 'error-message', reflect: true },
    compact:      { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.provider = '';
    this.state = 'disconnected';
    this.since = '';
    this.errorMessage = '';
    this.compact = false;
  }

  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: var(--crusher-spacing-3);
      padding: var(--crusher-spacing-2) var(--crusher-spacing-3);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-md);
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      font-size: var(--crusher-font-size-sm);
      line-height: var(--crusher-line-height-snug);
      min-width: 0;
    }
    :host([compact]) {
      padding: var(--crusher-spacing-1) var(--crusher-spacing-2);
      gap: var(--crusher-spacing-2);
    }

    .dot {
      flex: none;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: var(--crusher-radius-full);
      background: var(--crusher-fg-muted);
      box-shadow: 0 0 0 0.125rem color-mix(in srgb, var(--crusher-fg-muted) 18%, transparent);
    }
    :host([state="connected"]) .dot {
      background: var(--crusher-feedback-success);
      box-shadow: 0 0 0 0.125rem color-mix(in srgb, var(--crusher-feedback-success) 22%, transparent);
    }
    :host([state="reauth-required"]) .dot {
      background: var(--crusher-feedback-warning);
      box-shadow: 0 0 0 0.125rem color-mix(in srgb, var(--crusher-feedback-warning) 22%, transparent);
    }
    :host([state="error"]) .dot {
      background: var(--crusher-feedback-danger);
      box-shadow: 0 0 0 0.125rem color-mix(in srgb, var(--crusher-feedback-danger) 22%, transparent);
    }
    :host([state="connecting"]) .dot {
      background: var(--crusher-color-brand-primary);
      box-shadow: 0 0 0 0.125rem color-mix(in srgb, var(--crusher-color-brand-primary) 22%, transparent);
      animation: pulse 1.4s ease-in-out infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.4; }
    }
    @media (prefers-reduced-motion: reduce) {
      :host([state="connecting"]) .dot { animation: none; }
    }

    .body {
      display: flex;
      flex-direction: column;
      min-width: 0;
      gap: var(--crusher-spacing-1);
    }
    :host([compact]) .body {
      flex-direction: row;
      align-items: baseline;
      gap: var(--crusher-spacing-2);
    }

    .label {
      font-weight: var(--crusher-font-weight-medium);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .meta {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-xs);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    :host([compact]) .meta { font-size: var(--crusher-font-size-sm); }

    .cta {
      margin-inline-start: auto;
      flex: none;
      display: inline-flex;
    }
    .cta ::slotted(*) { font: inherit; }
  `;

  _labelFor(state) {
    switch (state) {
      case 'connected':       return 'Connected';
      case 'reauth-required': return 'Re-authorize required';
      case 'error':           return 'Connection error';
      case 'connecting':      return 'Connecting…';
      case 'disconnected':
      default:                return 'Not connected';
    }
  }

  _meta() {
    if (this.state === 'connected' && this.since) {
      return `${this.provider ? this.provider + ' · ' : ''}since ${this.since}`;
    }
    if (this.state === 'error' && this.errorMessage) {
      return `${this.provider ? this.provider + ' · ' : ''}${this.errorMessage}`;
    }
    if (this.provider) return this.provider;
    return '';
  }

  /* Per ARIA APG, "status" is the right live-region role for a non-interactive
     status that updates passively. The label includes both state + provider. */
  render() {
    const state = this.state || 'disconnected';
    const label = this._labelFor(state);
    const meta = this._meta();
    const ariaLabel = `${label}${this.provider ? ' — ' + this.provider : ''}`;

    return html`
      <span class="dot" aria-hidden="true"></span>
      <span class="body" role="status" aria-live="polite" aria-label=${ariaLabel}>
        <span class="label">${label}</span>
        ${meta ? html`<span class="meta">${meta}</span>` : null}
      </span>
      <span class="cta"><slot name="cta"></slot></span>
    `;
  }
}

customElements.define('crusher-connection-status', CrusherConnectionStatus);
